import { Link } from "react-router-dom";
import { ShieldCheck, Users, Wrench, Zap, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { CONTACT } from "../data/site";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Safety First",
    body: "Every job is completed to AS/NZS 3000 and comes with a Certificate of Electrical Safety. No shortcuts.",
  },
  {
    icon: Users,
    title: "Straight Talking",
    body: "You'll speak to the person doing the work. Real quotes, honest advice, no upsells.",
  },
  {
    icon: Wrench,
    title: "Proper Craft",
    body: "Neat cable runs, labelled circuits, clean finishes. If it's not right, it's not done.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Split hero */}
      <section className="border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 py-20 md:py-28 items-center">
          <div className="lg:col-span-7">
            <p className="overline"><span className="text-[#E62117] mr-2">◆</span>About Andstel Group</p>
            <h1 data-testid="about-title" className="font-display text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.88] mt-6">
              Melbourne trade.<br />
              <span className="text-[#E62117]">Melbourne trust.</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-700 leading-relaxed max-w-lg">
              Andstel Group is a Melbourne-based electrical contractor specialising in solar,
              battery storage and EV infrastructure. Family-run, tradie-owned, and built on
              word-of-mouth from Sunshine to Sorrento.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="about-cta-contact" className="btn-primary">
                Work With Us <ArrowUpRight size={16} className="ml-2" />
              </Link>
              <a href={`tel:${CONTACT.phoneRaw}`} data-testid="about-cta-call" className="btn-secondary">
                Call {CONTACT.phone}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border-2 border-black shadow-[12px_12px_0px_0px_rgba(230,33,23,1)] aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxlbGVjdHJpY2lhbiUyMHdvcmtpbmd8ZW58MHx8fHwxNzgyOTAxNDU5fDA&ixlib=rb-4.1.0&q=85"
                alt="Andstel Group electrician at work"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32 bg-zinc-50 border-b-2 border-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="overline">The story so far</p>
          <div className="mt-6 font-mono-brand text-base md:text-lg leading-loose text-zinc-800 space-y-6">
            <p>
              <span className="text-[#E62117] font-bold">// 01</span> — Founded by Andrew, a Victorian-licensed electrician
              with a passion for renewables and doing things the right way. What started as
              residential jobs in Melbourne's west has grown into a full-service electrical, solar and battery
              specialist covering all of Victoria.
            </p>
            <p>
              <span className="text-[#E62117] font-bold">// 02</span> — We take on jobs of every size — from a single
              powerpoint upgrade to full-home solar and battery integrations. Every project gets the
              same care, the same paperwork, and the same follow-through.
            </p>
            <p>
              <span className="text-[#E62117] font-bold">// 03</span> — We're Clean Energy Council accredited installers,
              approved for major manufacturer warranties, and proudly Australian-owned. Every switchboard
              upgrade we do is one more home that's safer, more efficient, and ready for the future.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-white border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="overline"><span className="text-[#E62117] mr-2">◆</span>What we stand for</p>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase mt-4 leading-[0.95] max-w-3xl">
            Three rules. No exceptions.
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} data-testid={`value-card-${i}`} className="card-industrial">
                  <div className="w-14 h-14 border-2 border-black flex items-center justify-center bg-black text-[#E62117]">
                    <Icon size={26} />
                  </div>
                  <h3 className="font-display text-3xl uppercase mt-6">{v.title}</h3>
                  <p className="mt-4 text-zinc-700 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-24 bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <p className="overline text-zinc-500">Credentials</p>
            <h2 className="font-display text-5xl md:text-6xl uppercase mt-4 leading-[0.95]">
              Certified.<br />Licensed.<br />Accountable.
            </h2>
          </div>
          <ul className="space-y-4">
            {[
              "Registered Electrical Contractor (VIC)",
              "Clean Energy Council Accredited Installer",
              "Tesla Powerwall Certified Installer",
              "$20M Public Liability Insurance",
              `ABN ${CONTACT.abn}`,
              "Certificate of Electrical Safety on every job",
            ].map((c, i) => (
              <li key={i} className="flex items-start gap-4 border-b border-zinc-800 pb-4">
                <CheckCircle2 className="text-[#E62117] flex-shrink-0 mt-1" size={22} />
                <span className="text-lg">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
